'use client';

import { ICountdownParams } from '@/interfaces';
import { IKeyBannerProperty, TimerFormatType } from '@/interfaces/homepage';
import { IconClock } from '@tabler/icons-react';
import { Fragment, useEffect, useState } from 'react';
import Countdown, { zeroPad } from 'react-countdown';

export const CountDownRenderer = ({
  days,
  hours,
  minutes,
  seconds,
  isHomepage,
  timerFormat,
}: ICountdownParams & { isHomepage?: boolean; timerFormat?: TimerFormatType }) => {
  const format = timerFormat ?? 'DHMS';
  const showDays = format === 'DHMS' || format === 'DHM';
  const showHours = format === 'DHMS' || format === 'HMS' || format === 'DHM';

  const totalHours = (Number(days) || 0) * 24 + (Number(hours) || 0);
  const totalMinutes = totalHours * 60 + (Number(minutes) || 0);

  const displayHours = showDays ? hours : totalHours;
  const displayMinutes = showHours ? minutes : totalMinutes;

  const visibleUnits: Record<TimerFormatType, string[]> = {
    DHMS: ['Days', 'Hours', 'Minutes', 'Seconds'],
    HMS: ['Hours', 'Minutes', 'Seconds'],
    MS: ['Minutes', 'Seconds'],
    DHM: ['Days', 'Hours', 'Minutes'],
  };

  const arr = [
    { value: days, name: 'Days' },
    { value: displayHours, name: 'Hours' },
    { value: displayMinutes, name: 'Minutes' },
    { value: seconds, name: 'Seconds' },
  ];

  const visibleArr = arr.filter((u) => visibleUnits[format].includes(u.name));

  if (isHomepage) {
    return (
      <div className="flex items-start justify-center gap-1 md:gap-2">
        {visibleArr.map((item, index) => {
          return (
            <Fragment key={`${index}-${item.name}`}>
              {index != 0 && <span className="flex h-9 items-center justify-center self-start text-2xl font-extrabold md:h-14 md:text-5xl">:</span>}
              <div className="flex w-16 flex-col items-center text-center md:w-20">
                <div className="flex h-9 items-center justify-center md:h-14">
                  <span className="text-2xl font-extrabold md:text-5xl">{zeroPad(item.value)}</span>
                </div>
                <div className="text-base md:text-lg">{item.name}</div>
              </div>
            </Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex w-[92px] items-center justify-center px-3 py-0.5 text-lg font-medium md:w-[120px] md:py-1.5 md:text-2xl">
      {visibleArr.map((item, index) => {
        return (
          <Fragment key={`${index}-${item.name}`}>
            {index != 0 && <p>:</p>}
            <p>{zeroPad(item.value)}</p>
          </Fragment>
        );
      })}
    </div>
  );
};

export function CountdownTimer({ content, isHomepage = false }: Readonly<{ content?: Readonly<IKeyBannerProperty>; isHomepage?: boolean }>) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => setIsMounted(true), []);

  if (!isMounted || !content?.EndDateUtc || !content.ShowTimer || new Date(content.EndDateUtc) < new Date()) {
    return null;
  }
  if (isHomepage) {
    return (
      <div className="relative z-[38] h-full w-full">
        <div className="absolute bottom-4 right-0 sm:bottom-12">
          <div
            style={{ backgroundColor: content?.TimerBackgroundColor || '#000000' }}
            className="h-full w-full overflow-hidden rounded-xl px-6 py-4 md:px-14 md:py-7"
          >
            <div className="relative bottom-0 right-0 z-50 space-y-3 md:space-y-5" style={{ color: content?.TimerTextColor || '#fde047' }}>
              <div className="flex items-center justify-center gap-2 leading-none">
                <IconClock className="h-5 w-5 shrink-0 sm:h-[22px] sm:w-[22px] md:h-[26px] md:w-[26px]" />
                <span className="text-center text-lg font-bold sm:text-xl md:text-2xl">OFFER ENDS IN</span>
              </div>
              <Countdown
                date={content?.EndDateUtc}
                renderer={(props) => <CountDownRenderer {...props} isHomepage={isHomepage} timerFormat={content?.TimerFormat} />}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ backgroundColor: content?.TextBackgroundColor || '#000000' }}
      className="mb-8 flex w-full flex-wrap items-center justify-center gap-x-5 gap-y-1 px-8 py-2.5 md:mb-10 md:py-4 lg:mb-12"
    >
      <p style={{ color: content?.TextColor || '#000000' }} className="border-r border-white/20 pr-4 text-center text-xl font-medium md:text-3xl">
        {content?.Text || 'Limited-time offers on selected products'}
      </p>

      <div
        className="rounded-md md:rounded-lg"
        style={{ backgroundColor: content?.TimerBackgroundColor || '#dc2626', color: content?.TimerTextColor || '#000000' }}
      >
        <Countdown
          date={content?.EndDateUtc}
          renderer={(props) => <CountDownRenderer {...props} isHomepage={isHomepage} timerFormat={content?.TimerFormat} />}
        />
      </div>
    </div>
  );
}
